import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Menu, Save, RotateCcw, Settings, Home, Sliders, FolderOpen, Banknote, LogOut,
} from 'lucide-react';
import { useAuthStore } from '@/state/authStore';
import type { LucideIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/state/gameStore';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SandboxSettingsModal } from './SandboxSettingsModal';
import { SettingsModal } from './SettingsModal';
import { SaveBrowser } from './SaveBrowser';
import { LoanDialog } from './LoanDialog';

/**
 * Bottom-left cluster:
 *  - Primary Dashboard FAB (large, accent) → opens main panel.
 *    Hidden when mainPanelOpen is true.
 *  - Secondary Burger FAB (smaller, neutral) above the primary.
 *    Stays visible regardless of panel state.
 */
export function FabCluster() {
  const mainPanelOpen = useGameStore((s) => s.mainPanelOpen);
  const openMainPanel = useGameStore((s) => s.openMainPanel);

  return (
    <div className="pointer-events-none fixed bottom-4 left-4 z-30 flex flex-col items-start gap-2.5">
      <BurgerMenu />
      <AnimatePresence>
        {!mainPanelOpen && (
          <motion.button
            key="dashboard-fab"
            type="button"
            onClick={() => openMainPanel()}
            aria-label="Dispatch24 OS öffnen"
            className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-emergency text-white shadow-2xl ring-2 ring-accent-emergency/30 transition hover:bg-accent-emergency-hover focus:outline-none focus-visible:ring-4"
            initial={{ opacity: 0, scale: 0.8, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <LayoutDashboard className="h-6 w-6" aria-hidden />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

function BurgerMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [sandboxOpen, setSandboxOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveBrowserOpen, setSaveBrowserOpen] = useState(false);
  const [loanOpen, setLoanOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const mode = useGameStore((s) => s.mode);
  const resetGame = useGameStore((s) => s.resetGame);
  const pushToast = useGameStore((s) => s.pushToast);
  const tester = useAuthStore((s) => s.tester);
  const logout = useAuthStore((s) => s.logout);
  const isSandbox = mode === 'sandbox';

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <div ref={containerRef} className="pointer-events-auto relative">
        <button
          type="button"
          aria-label="Menü"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-bg-elevated text-fg-primary shadow-lg backdrop-blur-md transition hover:border-border-strong hover:bg-bg-elevated/80"
          style={{ background: 'rgba(20, 24, 36, 0.9)' }}
        >
          <Menu className="h-4 w-4" aria-hidden />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              role="menu"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.15 }}
              className="absolute bottom-12 left-0 w-56 overflow-hidden rounded-md border border-border-strong bg-bg-secondary shadow-2xl"
            >
              {tester && (
                <div className="border-b border-border-subtle bg-bg-elevated/40 px-3 py-2.5 text-[11px] leading-tight">
                  <p className="text-fg-muted">Eingeloggt als</p>
                  <p className="truncate font-semibold text-fg-primary">{tester.name}</p>
                  <p className="truncate font-mono text-[10px] text-fg-muted">{tester.code}</p>
                </div>
              )}
              <MenuItem
                icon={Save}
                onClick={() => {
                  pushToast({ kind: 'success', text: 'Spielstand gespeichert.' });
                  setOpen(false);
                }}
              >
                Speichern
              </MenuItem>
              <MenuItem
                icon={FolderOpen}
                onClick={() => {
                  setSaveBrowserOpen(true);
                  setOpen(false);
                }}
              >
                Spielstände verwalten
              </MenuItem>
              {/* Phase 11.4.4 (Bug 16) — explanatory tooltip + sub-text in Sandbox. */}
              <MenuItem
                icon={Banknote}
                disabled={isSandbox}
                title={
                  isSandbox
                    ? 'In Sandbox nicht verfügbar — unbegrenztes Geld'
                    : 'Bank · Kredite verwalten'
                }
                onClick={() => {
                  setLoanOpen(true);
                  setOpen(false);
                }}
              >
                <span className="flex flex-col leading-tight">
                  <span>Kredite</span>
                  {isSandbox && (
                    <span className="text-[10px] text-fg-muted">
                      In Sandbox nicht verfügbar — unbegrenztes Geld
                    </span>
                  )}
                </span>
                {isSandbox && (
                  <span className="ml-auto text-[10px] text-fg-muted">Sandbox</span>
                )}
              </MenuItem>
              <MenuItem
                icon={Settings}
                onClick={() => {
                  setSettingsOpen(true);
                  setOpen(false);
                }}
              >
                Einstellungen
              </MenuItem>
              <MenuItem
                icon={Sliders}
                disabled={!isSandbox}
                title={isSandbox ? 'Sandbox-spezifische Einstellungen' : 'Nur im Sandbox-Modus verfügbar'}
                onClick={() => {
                  setSandboxOpen(true);
                  setOpen(false);
                }}
              >
                Sandbox-Einstellungen
                {!isSandbox && (
                  <span className="ml-auto text-[10px] text-fg-muted">Sandbox</span>
                )}
              </MenuItem>
              <MenuItem icon={RotateCcw} onClick={() => setConfirmReset(true)} tone="danger">
                Spiel neu starten
              </MenuItem>
              <div className="border-t border-border-subtle" />
              <MenuItem icon={Home} onClick={() => navigate('/')}>
                Zur Startseite
              </MenuItem>
              {tester && (
                <MenuItem
                  icon={LogOut}
                  onClick={async () => {
                    setOpen(false);
                    await logout();
                    pushToast({ kind: 'info', text: 'Abgemeldet.' });
                    navigate('/');
                  }}
                  tone="danger"
                >
                  Abmelden
                </MenuItem>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ConfirmDialog
        open={confirmReset}
        title="Spiel neu starten?"
        description="Dein aktueller Spielstand wird gelöscht. Diese Aktion kann nicht rückgängig gemacht werden."
        confirmLabel="Neu starten"
        tone="danger"
        onConfirm={() => {
          resetGame();
          setConfirmReset(false);
          setOpen(false);
          pushToast({ kind: 'info', text: 'Neues Spiel gestartet.' });
        }}
        onCancel={() => setConfirmReset(false)}
      />

      <SandboxSettingsModal open={sandboxOpen} onClose={() => setSandboxOpen(false)} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <SaveBrowser open={saveBrowserOpen} onClose={() => setSaveBrowserOpen(false)} />
      <LoanDialog open={loanOpen} onClose={() => setLoanOpen(false)} />
    </>
  );
}

interface MenuItemProps {
  icon: LucideIcon;
  children: React.ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  title?: string;
}

function MenuItem({
  icon: Icon,
  children,
  onClick,
  tone = 'default',
  disabled,
  title,
}: MenuItemProps) {
  const toneClass =
    tone === 'danger' ? 'text-accent-emergency hover:bg-accent-emergency/10' : 'hover:bg-bg-elevated';
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={[
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition',
        disabled ? 'cursor-not-allowed text-fg-muted' : toneClass,
      ].join(' ')}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {children}
    </button>
  );
}
